import { io, Socket } from 'socket.io-client';
import { Message, Chat } from '../types';

type SocketCallback = (...args: any[]) => void;

class SocketService {
  private socket: Socket | null = null;
  private listeners: Map<string, SocketCallback[]> = new Map();
  // Подписчики на «переподключились после разрыва — догрузи пропущенное».
  private resyncCallbacks: SocketCallback[] = [];
  private hasConnectedOnce = false;

  connect() {
    // Token is in HttpOnly cookie, sent automatically via withCredentials

    // Reuse existing socket instance if possible
    if (this.socket) {
      if (!this.socket.connected) {
        this.socket.connect();
      }
      return;
    }

    // Prefer WebSocket and keep polling as a fallback for restrictive proxies
    this.socket = io('/', {
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnection: true,
      // Было 10 попыток: после разрыва подольше (сон ноутбука, смена сети)
      // socket.io сдавался НАВСЕГДА, и вкладка молча немела до F5 — оператор
      // видел замороженную ленту и уходил отвечать в Telegram. Сдаваться нельзя.
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      timeout: 10000,
    });

    this.socket.on('connect', () => {
      console.log('Socket.IO connected');
      if (this.hasConnectedOnce) {
        // События, отправленные за время разрыва, socket.io НЕ переигрывает —
        // они потеряны безвозвратно. Единственный способ их увидеть — перечитать
        // данные запросом.
        console.log('Socket.IO reconnected — догружаем пропущенное');
        this.resyncCallbacks.forEach(cb => {
          try {
            cb();
          } catch (e) {
            console.error('Socket.IO resync callback failed:', e);
          }
        });
      }
      this.hasConnectedOnce = true;
    });

    this.socket.on('disconnect', () => {
      console.log('Socket.IO disconnected');
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket.IO connection error:', error);
      // Allow explicit reconnect after auth refresh/login
      if (String(error?.message || '').toLowerCase().includes('unauthorized')) {
        this.socket?.disconnect();
      }
    });

    // Restore listeners
    this.listeners.forEach((callbacks, event) => {
      callbacks.forEach(callback => {
        this.socket?.on(event, callback);
      });
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  on(event: string, callback: SocketCallback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }

    const callbacks = this.listeners.get(event)!;
    if (callbacks.includes(callback)) {
      return;
    }
    callbacks.push(callback);

    if (this.socket) {
      this.socket.on(event, callback);
    }
  }

  off(event: string, callback?: SocketCallback) {
    if (callback) {
      const callbacks = this.listeners.get(event);
      if (callbacks) {
        const index = callbacks.indexOf(callback);
        if (index > -1) {
          callbacks.splice(index, 1);
        }
      }
      this.socket?.off(event, callback);
    } else {
      this.listeners.delete(event);
      this.socket?.off(event);
    }
  }

  emit(event: string, data: any) {
    this.socket?.emit(event, data);
  }

  /**
   * Вызывается после КАЖДОГО переподключения (но не после первого соединения).
   * Здесь нужно перечитать данные: всё, что прилетело за время разрыва, потеряно.
   */
  onReconnect(callback: SocketCallback) {
    if (!this.resyncCallbacks.includes(callback)) {
      this.resyncCallbacks.push(callback);
    }
  }

  offReconnect(callback: SocketCallback) {
    const index = this.resyncCallbacks.indexOf(callback);
    if (index > -1) {
      this.resyncCallbacks.splice(index, 1);
    }
  }

  /** Живо ли соединение прямо сейчас (для страховочного опроса). */
  isConnected(): boolean {
    return Boolean(this.socket?.connected);
  }

  // Typed event listeners
  onNewMessage(callback: (message: Message) => void) {
    this.on('new_message', callback as SocketCallback);
  }

  onMessageUpdate(callback: (message: Message) => void) {
    this.on('message_update', callback);
  }

  onChatUpdate(callback: (chat: Chat) => void) {
    this.on('chat_update', callback);
  }

  onChatAssigned(callback: (data: { chat_id: number; user_id: number | null }) => void) {
    this.on('chat_assigned', callback);
  }

  onUserTyping(callback: (data: { chat_id: number; user: string }) => void) {
    this.on('user_typing', callback);
  }

  onMessageEdited(callback: (data: { chat_id: number; message_id: number; text: string }) => void) {
    this.on('message_edited', callback);
  }

  onMessagePinned(callback: (data: { chat_id: number; message_id: number; value: boolean }) => void) {
    this.on('message_pinned', callback);
  }

  onMessageDeleted(callback: (data: { chat_id: number; message_id: number }) => void) {
    this.on('message_deleted', callback);
  }

  // Emit typed events
  sendTyping(chatId: number) {
    this.emit('typing', { chat_id: chatId });
  }

  markAsRead(chatId: number) {
    this.emit('mark_as_read', { chat_id: chatId });
  }
}

const socketService = new SocketService();
export default socketService;
